import { useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle, Mail, Loader2, ShieldCheck, TrendingUp, Clock, Calendar,
  Layers, ArrowRight, Sparkles, Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SubstackHeaderButton, SubstackSlimFooter } from "@/components/SubstackLink";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/FsGuR37Niq0fFY5F2tii";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.7 }, viewport: { once: true } };

const RidgemontLogo = ({ className = "" }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 12 L88 82 L12 82 Z" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
    <path d="M28 62 L72 62" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const AnnuitiesPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', state: '', situation: '' });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Required';
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Valid email required';
    if (!formData.phone.trim()) newErrors.phone = 'Required';
    if (!formData.state) newErrors.state = 'Required';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/leads/annuity-guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (window.dataLayer) window.dataLayer.push({ event: 'annuity_guide_lead' });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setErrors({ submit: 'Something went wrong. Please try again or email hello@ridgemontfinancial.com.' });
    } finally {
      setSubmitting(false);
    }
  };

  const annuityTypes = [
    {
      icon: ShieldCheck,
      name: "Fixed Annuities (MYGA)",
      tagline: "Certainty. Locked-in rate.",
      desc: "A contractually defined interest rate for a specific term (typically 3-10 years). Like a CD, but issued by a life insurance carrier with tax-deferred growth.",
      bestFor: "Savers who want a known outcome and predictable growth without market risk.",
      tradeoff: "Lower upside than market-linked strategies."
    },
    {
      icon: TrendingUp,
      name: "Fixed Indexed Annuities (FIA)",
      tagline: "Market-linked growth. Downside floor.",
      desc: "Interest credits tied to a market index (like the S&P 500) with a 0% floor designed to help limit losses in a down year. Optional income riders can add lifetime income.",
      bestFor: "Pre-retirees who want participation in market gains without absorbing market losses.",
      tradeoff: "Growth is capped or participation-limited by contract."
    },
    {
      icon: Clock,
      name: "Immediate Annuities (SPIA)",
      tagline: "Turn savings into a paycheck. Today.",
      desc: "Deposit a lump sum and receive contractually defined monthly income beginning within 12 months. Can be structured for life, or for a set number of years.",
      bestFor: "Retirees who need income to start now and want it structured to continue for life.",
      tradeoff: "Once purchased, the principal typically isn't accessible outside the payment schedule."
    },
    {
      icon: Calendar,
      name: "Deferred Income Annuities (DIA)",
      tagline: "Buy income today. Turn it on later.",
      desc: "Similar to an immediate annuity, but income starts at a future date (5-20+ years from purchase). The delay creates significantly higher lifetime income per dollar.",
      bestFor: "Pre-retirees 45-60 who want to lock in a future income floor at today's rates.",
      tradeoff: "Money is tied up until the income start date."
    }
  ];

  return (
    <div className="bg-white" data-testid="annuities-page">
      {/* Slim top nav */}
      <header className="bg-white border-b border-[#E7E5E4] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="logo-home">
            <RidgemontLogo className="w-8 h-8 text-[#0A192F]" />
            <div className="flex flex-col leading-none">
              <span className="text-xl font-light tracking-tight text-[#0A192F]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ridgemont</span>
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#C5A059] mt-0.5">Financial</span>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <SubstackHeaderButton />
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="btn-accent px-5 py-2 md:px-6 md:py-5 text-sm md:text-base" data-testid="nav-cta">
                Schedule Call
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0A192F] via-[#0A192F] to-[#1B4D3E]">
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <div className="absolute top-1/4 -left-32 w-[700px] h-[700px] bg-[#C5A059] rounded-full blur-[200px]" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-5">
                Retirement Annuities &middot; Independent &amp; Objective
              </p>
              <h1 className="text-[36px] sm:text-5xl lg:text-[58px] xl:text-[64px] font-light text-white leading-[1.05] tracking-[-0.01em] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Find the right annuity
                <span className="block text-[#C5A059]">— or right mix — for your peace of mind.</span>
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-6 max-w-xl">
                Annuities aren't one product &mdash; they're a category. The wrong one is expensive.
                The right one (or combination of a few) can turn a retirement portfolio
                into a paycheck you can actually count on.
              </p>
              <p className="text-white/60 text-base leading-relaxed max-w-xl">
                We shop <span className="text-[#C5A059] font-medium">12+ leading carriers</span> to match you with the exact contract features
                that fit your situation &mdash; not the one that pays us the highest commission.
              </p>
            </motion.div>

            {/* Lead magnet form */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
              {submitted ? (
                <div className="bg-white p-8 md:p-10 shadow-2xl text-center" data-testid="guide-success">
                  <div className="w-16 h-16 bg-[#1B4D3E] flex items-center justify-center mx-auto mb-5 rounded-full">
                    <Mail className="w-8 h-8 text-[#C5A059]" />
                  </div>
                  <h2 className="text-2xl font-light text-[#0A192F] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Guide is on the way.
                  </h2>
                  <p className="text-[#57534E] leading-relaxed mb-4">
                    We'll email <span className="font-medium text-[#0A192F]">{formData.email}</span> your copy of
                    the <em>Annuity Decision Guide</em> within one business day, along with a personalized note.
                  </p>
                  <p className="text-[#57534E] text-sm">
                    A Ridgemont advisor will follow up shortly to answer any questions and see if a quick call would be helpful.
                    <span className="block mt-2">No pressure. No pitch.</span>
                  </p>
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-block mt-6">
                    <Button className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-8 py-6 font-semibold">
                      <Calendar className="w-5 h-5 mr-2" />
                      Book a Call While You Wait
                    </Button>
                  </a>
                </div>
              ) : (
                <div className="bg-white p-7 md:p-8 shadow-2xl" data-testid="guide-form">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-[#C5A059]" />
                    <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059] font-semibold">Free Guide</p>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-light text-[#0A192F] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    The Annuity Decision Guide
                  </h2>
                  <p className="text-sm text-[#57534E] mb-5">
                    Which annuity type fits your situation? Get our plain-English guide + a 1:1 personalized recommendation.
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Input name="name" placeholder="Full Name *" value={formData.name} onChange={handleChange} className={errors.name ? 'border-red-500' : ''} data-testid="guide-name" />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <Input name="email" type="email" placeholder="Email *" value={formData.email} onChange={handleChange} className={errors.email ? 'border-red-500' : ''} data-testid="guide-email" />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Input name="phone" type="tel" placeholder="Phone *" value={formData.phone} onChange={handleChange} className={errors.phone ? 'border-red-500' : ''} data-testid="guide-phone" />
                        {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                      </div>
                      <div>
                        <Select value={formData.state} onValueChange={(val) => { setFormData(prev => ({ ...prev, state: val })); if (errors.state) setErrors(prev => ({ ...prev, state: '' })); }}>
                          <SelectTrigger className={errors.state ? 'border-red-500' : ''} data-testid="guide-state">
                            <SelectValue placeholder="State of Residence *" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[260px]">
                            {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                      </div>
                    </div>
                    <div>
                      <textarea name="situation" placeholder="Your situation (optional) — e.g., rolling over a 401(k), looking for income at 62..."
                        value={formData.situation} onChange={handleChange}
                        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm min-h-[72px] resize-y focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
                        data-testid="guide-situation" />
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-[#0A192F] text-white hover:bg-[#0A192F]/90 py-6 text-base font-semibold" data-testid="guide-submit">
                      {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending...</> : <>Email Me the Guide</>}
                    </Button>
                    {errors.submit && <p className="text-red-500 text-xs text-center">{errors.submit}</p>}
                    <p className="text-[11px] text-[#57534E]/80 text-center leading-relaxed">
                      Instant email delivery. No obligation. Advisor follow-up only if you want one.
                    </p>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT ANNUITIES DO */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-4">Start Here</p>
            <h2 className="text-3xl md:text-5xl font-light text-[#0A192F] tracking-tight mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What is an annuity, really?
            </h2>
            <p className="text-lg text-[#57534E] leading-relaxed max-w-2xl mx-auto">
              An annuity is a <span className="text-[#0A192F] font-medium">contract with a life insurance carrier</span>. You deposit money.
              In exchange, the carrier commits contractually to either grow your money on defined terms,
              pay you income for a defined period, or both. That's it.
            </p>
            <p className="text-base text-[#57534E] leading-relaxed max-w-2xl mx-auto mt-4">
              The complexity isn't the concept &mdash; it's the <em>choices</em>. Fixed rate or market-linked? Income now or later?
              For life or for a set term? Which carrier? Which rider? That's where an independent advisor pays for themselves.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FOUR TYPES */}
      <section className="py-20 md:py-24 bg-[#FAFAF9]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-4">The Four Types</p>
            <h2 className="text-3xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Each does one thing extremely well.
            </h2>
            <p className="text-lg text-[#57534E] mt-4 max-w-2xl mx-auto">
              Most retirement plans use a <span className="text-[#0A192F] font-medium">mix</span> of these &mdash; not just one.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {annuityTypes.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-[#E7E5E4] p-7 md:p-8"
                data-testid={`annuity-type-${i}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#0A192F] flex items-center justify-center flex-shrink-0">
                    <a.icon className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-[#0A192F]">{a.name}</h3>
                    <p className="text-sm text-[#C5A059] font-medium mt-1">{a.tagline}</p>
                  </div>
                </div>
                <p className="text-[#57534E] leading-relaxed text-sm mb-4">{a.desc}</p>
                <div className="space-y-2 pt-4 border-t border-[#E7E5E4]">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#1B4D3E]" />
                    <p className="text-xs text-[#0A192F]"><span className="font-semibold">Best for:</span> <span className="text-[#57534E]">{a.bestFor}</span></p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Layers className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#C5A059]" />
                    <p className="text-xs text-[#0A192F]"><span className="font-semibold">Tradeoff:</span> <span className="text-[#57534E]">{a.tradeoff}</span></p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY MIX + OUR APPROACH */}
      <section className="py-20 md:py-24 bg-[#0A192F] text-white">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-4">Our Approach</p>
            <h2 className="text-3xl md:text-5xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              A well-built retirement usually
              <span className="block text-[#C5A059]">uses more than one.</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, title: "Independent, not captive", desc: "We're appointed with 12+ carriers. That means we can shop your situation across the market — not push whichever product a single carrier is promoting." },
              { icon: TrendingUp, title: "Fit before features", desc: "We start with your income needs, timeline, and risk tolerance. The product comes last, once the plan is clear." },
              { icon: Award, title: "Transparent tradeoffs", desc: "Every annuity has a cost. We tell you what it is — liquidity, upside cap, commission — so you make the decision with your eyes open." }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.12 }} viewport={{ once: true }} className="bg-white/5 border border-white/10 p-7">
                <item.icon className="w-8 h-8 text-[#C5A059] mb-4" />
                <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                <p className="text-white/70 leading-relaxed text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 bg-[#FAFAF9]">
        <div className="max-w-[900px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-4">Common Questions</p>
            <h2 className="text-3xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What people usually ask us.
            </h2>
          </motion.div>
          <div className="space-y-4">
            {[
              { q: "Are annuities a good deal or a bad deal?", a: "Neither, on their own. Like any financial product, annuities are the right answer for some situations and the wrong answer for others. The question isn't 'annuity yes/no' — it's 'which annuity, from which carrier, with which features, at what percentage of your total portfolio.' That's the conversation we have." },
              { q: "Aren't annuity commissions high?", a: "Commissions are baked into the product and vary by carrier and contract type. What matters is whether the product's features justify the cost for your specific situation. We show you the tradeoffs plainly — you decide." },
              { q: "What if I need the money back?", a: "Every annuity has liquidity rules — most allow annual withdrawals of 10% without surrender charges, with full liquidity available after the surrender period (typically 5-10 years). We match the surrender schedule to your actual timeline, not the carrier's preferred one." },
              { q: "How does the guide work — do you actually email it?", a: "Yes. When you submit the form, we send you the Annuity Decision Guide by email within one business day, and a Ridgemont advisor personally follows up to answer questions. No auto-drips, no bots — a real conversation, only if you want one." },
              { q: "What's the guarantee?", a: "Annuity contract features are backed by the financial strength and claims-paying ability of the issuing insurance company. That's why carrier selection matters — we only work with A-rated (or higher) carriers." }
            ].map((item, i) => (
              <motion.details
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                viewport={{ once: true }}
                className="bg-white border border-[#E7E5E4] p-5 md:p-6 group"
                data-testid={`faq-${i}`}
              >
                <summary className="cursor-pointer font-medium text-[#0A192F] flex items-center justify-between list-none">
                  <span className="pr-4">{item.q}</span>
                  <span className="text-[#C5A059] text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-[#57534E] mt-3 leading-relaxed text-sm">{item.a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-24 bg-[#0A192F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#C5A059] rounded-full blur-[180px]" />
        </div>
        <div className="max-w-[900px] mx-auto px-4 md:px-8 text-center relative z-10">
          <motion.div {...fadeUp}>
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-5">Ready to Talk?</p>
            <h2 className="text-3xl md:text-5xl font-light text-white tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Two paths from here.
            </h2>
            <p className="text-white/70 mb-10 leading-relaxed max-w-2xl mx-auto">
              Read first, or talk first &mdash; whichever feels right. Either way, no pressure.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-10 py-7 text-lg font-semibold" data-testid="final-guide-cta">
                <Sparkles className="w-5 h-5 mr-2" />
                Get the Guide
              </Button>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-10 py-7 text-lg" data-testid="final-book-cta">
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule a Call
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Slim Footer */}
      <footer className="bg-[#0A192F] border-t border-white/10 py-8">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <p>&copy; {new Date().getFullYear()} Ridgemont Financial. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <a href="/privacy-policy" className="hover:text-[#C5A059] transition-colors">Privacy</a>
            <a href="/terms-of-service" className="hover:text-[#C5A059] transition-colors">Terms</a>
            <a href="mailto:hello@ridgemontfinancial.com" className="hover:text-[#C5A059] transition-colors flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" />hello@ridgemontfinancial.com
            </a>
            <SubstackSlimFooter />
          </div>
        </div>
        <p className="text-white/30 text-[10px] italic text-center mt-4 max-w-3xl mx-auto px-4">
          Annuity contract features are backed by the financial strength and claims-paying ability of the issuing insurance company.
          Product availability, riders, and provisions vary by carrier, product, and state. Not a solicitation for the sale of a security.
        </p>
      </footer>
    </div>
  );
};

export default AnnuitiesPage;
